/**
 * موجودیِ اولِ دوره — بخشِ جدا از پارت‌ها (کاربر v2.170: «بخشِ جدا باشد، همان بالا مثلاً در تنظیمات؛ اصلاً ربطی به پارت ندارد»).
 * هر ردیف = طاقهٔ خامِ یک نخ که قبل از شروعِ کار با برنامه (یا سالِ مالیِ تازه) در رنگرزی بوده. در تبِ «مانده» کنارِ همان نخ
 * می‌آید و در سفارش با برچسبِ «اول دوره <نخ>» انتخاب می‌شود؛ در فهرستِ پارت‌ها نمی‌آید.
 * (ذخیره: همان `dye_lots` با `is_opening` — تا سفارش/انتقال مثلِ پارت از آن کم کند.)
 */
import React, { useState } from "react";
import { Button, Input, Popconfirm, Space, message } from "antd";
import { DeleteOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import api from "../../api/client";
import { apiError } from "../../utils/apiError";
import { JalaliDateInput, todayISO } from "../../utils/dateUtils";
import { BASE, IntInput, QuickPick, fa, n0 } from "./dyeShared";

const EMPTY = { entry_date: null, yarn: null, tagheh: null, dyehouse: null, notes: null };

function Row({ l, canEdit, canDelete, onSaved }) {
  const [t, setT] = useState(l.tagheh);
  const run = async (fn, ok) => { try { await fn(); message.success(ok); await onSaved(); } catch (e) { message.error(apiError(e)); } };
  return (
    <div className="dye-card" style={{ cursor: "default" }}>
      <div className="dye-card-h">
        <span className="dye-label">نخ {fa(l.yarn || "—")}</span>
        <span className="dye-mute">{fa(l.entry_date_fa)}{l.dyehouse ? ` · ${l.dyehouse}` : ""}</span>
        <span className="dye-grow" />
        <span className={"dye-rem" + (l.remaining > 0 ? " dye-pos" : l.remaining < 0 ? " dye-neg" : "")}>مانده {fa(l.remaining)}</span>
      </div>
      <div className="dye-card-f">
        <span>سفارش‌شده {fa(l.ordered)}</span>
        {n0(l.transfer_out) > 0 && <span>منتقل‌شده {fa(l.transfer_out)}</span>}
        {l.notes && <span className="dye-mute">{l.notes}</span>}
      </div>
      {canEdit && (
        <Space.Compact style={{ width: "100%", marginTop: 8 }}>
          <IntInput value={t} onChange={setT} placeholder="طاقهٔ اولِ دوره" />
          <Button size="large" icon={<SaveOutlined />} disabled={t === l.tagheh || t == null}
            onClick={() => run(() => api.patch(`${BASE}/lots/${l.id}`, { tagheh: t }), "ذخیره شد")} />
          {canDelete && (
            <Popconfirm title="این موجودی حذف شود؟" onConfirm={() => run(() => api.delete(`${BASE}/lots/${l.id}`), "حذف شد")}>
              <Button size="large" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space.Compact>
      )}
    </div>
  );
}

export default function DyeOpening({ board, options, onSaved, canEdit, canDelete }) {
  const rows = board.lots.filter((l) => l.is_opening);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v === "" ? null : v }));
  const add = async () => {
    if (!f.entry_date || !f.yarn || !f.tagheh) return message.warning("تاریخ، نخ و تعدادِ طاقه لازم است");
    setBusy(true);
    try {
      await api.post(`${BASE}/lots`, { ...f, is_opening: true });
      message.success("موجودیِ اولِ دوره ثبت شد");
      setF(null);
      await onSaved();
    } catch (e) { message.error(apiError(e)); }
    setBusy(false);
  };
  const total = rows.reduce((s, l) => s + n0(l.remaining), 0);

  return (
    <div>
      <div className="dye-mute" style={{ marginBottom: 8 }}>
        طاقهٔ خامی که قبل از شروعِ کار با برنامه (یا اولِ سالِ مالی) در رنگرزی بوده، به تفکیکِ نخ. این‌ها پارت نیستند؛ در «مانده» کنارِ
        همان نخ می‌آیند و موقعِ سفارش با نامِ «اول دوره» انتخاب می‌شوند.
      </div>
      {canEdit && !f && (
        <Button block type="primary" icon={<PlusOutlined />} style={{ marginBottom: 10 }}
          onClick={() => setF({ ...EMPTY, entry_date: todayISO() })}>افزودنِ موجودیِ اولِ دوره</Button>
      )}
      {f && (
        <div className="dye-box" style={{ marginBottom: 10 }}>
          <div className="dye-form">
            <div className="dye-f dye-half"><span>تاریخِ اولِ دوره *</span>
              <JalaliDateInput value={f.entry_date} onChange={set("entry_date")} style={{ width: "100%", minHeight: 40 }} /></div>
            <div className="dye-f dye-half"><span>تعداد طاقه *</span>
              <IntInput value={f.tagheh} onChange={set("tagheh")} /></div>
            <div className="dye-f"><span>نخ *</span>
              <QuickPick value={f.yarn} onChange={set("yarn")} options={options.yarn} chips={6} /></div>
            <div className="dye-f"><span>رنگرزی</span>
              <QuickPick value={f.dyehouse} onChange={set("dyehouse")} options={options.dyehouse} /></div>
            <div className="dye-f"><span>توضیح</span>
              <Input value={f.notes ?? ""} onChange={(e) => set("notes")(e.target.value)} /></div>
          </div>
          <Space>
            <Button type="primary" size="large" loading={busy} onClick={add}>ثبت</Button>
            <Button size="large" onClick={() => setF(null)}>انصراف</Button>
          </Space>
        </div>
      )}
      {rows.length === 0 ? <div className="dye-mute">موجودیِ اولِ دوره‌ای ثبت نشده.</div> : (
        <>
          <div className="dye-sec">مانده از اولِ دوره — {fa(total)} طاقه</div>
          {rows.map((l) => <Row key={`${l.id}-${l.tagheh}`} l={l} canEdit={canEdit} canDelete={canDelete} onSaved={onSaved} />)}
        </>
      )}
    </div>
  );
}
